/**
 * IndustryVideoTypeCastPicker — reusable picker that lets the user choose
 * an industry, then a matching video-type, then a cast mode (with actors
 * or product-only). Used by both:
 *
 *   • `ConceptGenerator.jsx` ("I'm not sure — give me ideas" path)
 *   • `ScriptInput.jsx`      ("I have a rough idea" path)  ← added Feb 2026
 *
 * The "rough idea" path was previously a bare textarea → Generate Script
 * button with no industry / video-type / cast context. Wiring this picker
 * in lets the LLM produce a script that's properly targeted at the user's
 * industry, video format, and cast preference — without needing the full
 * reference-fetching pipeline.
 *
 * Props
 * ─────
 *  value             — `{ industryId, videoTypeId, customVideoTypeName, castMode }`
 *  onChange(value)   — called with the FULL value object on any change
 *  className         — optional outer wrapper class
 *  stepOffset        — first step number (default 1; ScriptInput uses 3 so
 *                      the rendered headers say "3 · Industry" etc.)
 *  hideCastWhen      — optional predicate `(value) => bool` for hiding the
 *                      cast section (Concept Generator hides it until both
 *                      industry + video-type are picked).
 *
 * Selection state lives in the PARENT (controlled component) so the parent
 * can decide what to do with it (feed into prompt, persist on project,
 * etc.). Selecting an industry resets video-type and custom-video-type to
 * mirror the spec the design team agreed on.
 */
import React from 'react';
import { CONCEPT_CATALOG } from './conceptCatalog';

const TooltipCard = ({ title, subtitle, benefit, selected, onClick, testid }) => (
    <button
        type="button"
        onClick={onClick}
        className={`group relative text-left p-3 rounded-xl border transition-all ${
            selected
                ? 'bg-violet-500/15 border-violet-500/60 ring-1 ring-violet-500/30'
                : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10'
        }`}
        data-testid={testid}
    >
        <div className="font-semibold text-sm text-white leading-tight">{title}</div>
        {subtitle && (
            <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug line-clamp-2">{subtitle}</div>
        )}
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <div className="bg-zinc-900 border border-white/15 rounded-lg p-3 shadow-xl shadow-black/60">
                <div className="text-xs font-semibold text-white mb-1">{title}</div>
                {subtitle && <div className="text-[11px] text-zinc-300 leading-relaxed">{subtitle}</div>}
                {benefit && (
                    <div className="text-[11px] text-violet-300 mt-2 flex items-start gap-1">
                        <span>👉</span>
                        <span>{benefit}</span>
                    </div>
                )}
            </div>
        </div>
    </button>
);

export const IndustryVideoTypeCastPicker = ({
    value,
    onChange,
    className = '',
    stepOffset = 1,
    hideCastWhen,
}) => {
    const industryId         = value?.industryId         || null;
    const videoTypeId        = value?.videoTypeId        || null;
    const customVideoTypeName = value?.customVideoTypeName || '';
    const customIndustryName  = value?.customIndustryName  || '';
    const castMode           = value?.castMode           || 'with_actors';

    const industry  = CONCEPT_CATALOG.find(i => i.id === industryId) || null;
    const videoType = industry?.videoTypes.find(v => v.id === videoTypeId) || null;

    // Helper to emit the FULL value object on any change so the parent
    // always gets a complete snapshot — no need for separate setters.
    const emit = (patch) => {
        onChange?.({
            industryId,
            videoTypeId,
            customVideoTypeName,
            customIndustryName,
            castMode,
            ...patch,
        });
    };

    const stepInd     = stepOffset;
    const stepVidType = stepOffset + 1;
    const stepCast    = stepOffset + 2;

    const castHidden = typeof hideCastWhen === 'function'
        ? hideCastWhen(value)
        : !(industry && videoType);

    return (
        <div className={`space-y-6 ${className}`}>
            {/* ---------- Industry ---------- */}
            <section>
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        {stepInd} · Industry
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {CONCEPT_CATALOG.map(ind => (
                        <TooltipCard
                            key={ind.id}
                            title={`${ind.icon} ${ind.name}`}
                            subtitle={ind.desc}
                            benefit={ind.benefit}
                            selected={industryId === ind.id}
                            onClick={() => emit({
                                industryId: ind.id,
                                // Reset downstream selection when industry changes
                                // — the same video-type id may not exist under
                                // the new industry.
                                videoTypeId: null,
                                customVideoTypeName: '',
                                // Clear the custom-industry text when the user
                                // picks any preset tile (keeps it when they
                                // stay on "Other").
                                customIndustryName: ind.id === 'other-industry' ? customIndustryName : '',
                            })}
                            testid={`industry-card-${ind.id}`}
                        />
                    ))}
                </div>
                {industryId === 'other-industry' && (
                    <div className="mt-3" data-testid="custom-industry-wrap">
                        <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                            Describe your industry
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={customIndustryName}
                                onChange={(e) => emit({ customIndustryName: e.target.value.slice(0, 40) })}
                                maxLength={40}
                                placeholder="e.g. Renewable energy, Pet care, Logistics"
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pr-14 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all"
                                data-testid="custom-industry-input"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 tabular-nums" data-testid="custom-industry-counter">
                                {customIndustryName.length}/40
                            </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1.5">
                            This label is sent to the AI as the industry context (max 40 characters).
                        </p>
                    </div>
                )}
            </section>

            {/* ---------- Video Type ---------- */}
            {industry && (
                <section>
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                            {stepVidType} · Video type · <span className="text-zinc-500 normal-case tracking-normal">for {industry.name}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {industry.videoTypes.map(vt => (
                            <TooltipCard
                                key={vt.id}
                                title={vt.name}
                                subtitle={vt.desc}
                                benefit={vt.benefit}
                                selected={videoTypeId === vt.id}
                                onClick={() => emit({
                                    videoTypeId: vt.id,
                                    // Clear the custom-name field when the user
                                    // picks any non-custom tile.
                                    customVideoTypeName: vt.id === 'other-custom' ? customVideoTypeName : '',
                                })}
                                testid={`videotype-card-${vt.id}`}
                            />
                        ))}
                    </div>
                    {videoTypeId === 'other-custom' && (
                        <div className="mt-3" data-testid="custom-video-type-wrap">
                            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2 block">
                                Describe your custom video type
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={customVideoTypeName}
                                    onChange={(e) => emit({ customVideoTypeName: e.target.value.slice(0, 30) })}
                                    maxLength={30}
                                    placeholder="e.g. Behind-the-scenes reel"
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pr-14 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all"
                                    data-testid="custom-video-type-input"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 tabular-nums" data-testid="custom-video-type-counter">
                                    {customVideoTypeName.length}/30
                                </span>
                            </div>
                            <p className="text-[11px] text-zinc-500 mt-1.5">
                                This label will be sent to the AI as the video format (max 30 characters).
                            </p>
                        </div>
                    )}
                </section>
            )}

            {/* ---------- Cast ---------- */}
            {!castHidden && (
                <section>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3">
                        {stepCast} · Cast
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: 'with_actors',  label: 'With actor/s', desc: 'People can appear on screen. Default.' },
                            { id: 'product_only', label: 'Product only', desc: 'Zero people on screen. Off-screen narration allowed.' },
                        ].map(opt => {
                            const active = castMode === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => emit({ castMode: opt.id })}
                                    className={`group relative text-left px-4 py-3 rounded-xl border transition-all flex-1 min-w-[180px] ${
                                        active
                                            ? 'bg-violet-500/15 border-violet-500/60 ring-1 ring-violet-500/30'
                                            : 'bg-white/5 border-white/10 hover:border-white/25 hover:bg-white/10'
                                    }`}
                                    data-testid={`cast-mode-${opt.id}`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-3 h-3 rounded-full border-2 ${active ? 'border-violet-400 bg-violet-400' : 'border-zinc-500'}`} />
                                        <div className="font-semibold text-sm text-white">{opt.label}</div>
                                    </div>
                                    <div className="text-[11px] text-zinc-400 leading-snug">{opt.desc}</div>
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
};

/**
 * Helper — resolves the picker value into the human-readable strings the
 * `generate-script-async` prompt expects.  Returns `null` if the user
 * hasn't picked enough to send (no industry / no video-type).
 */
export const resolvePickerForPrompt = (value) => {
    if (!value?.industryId || !value?.videoTypeId) return null;
    const rawIndustry = CONCEPT_CATALOG.find(i => i.id === value.industryId);
    if (!rawIndustry) return null;
    // When the user picks "Other (custom industry)", swap the catalog name
    // for the free-text label they typed (capped at 40 chars on input).
    const industry = (rawIndustry.id === 'other-industry' && value.customIndustryName?.trim())
        ? { ...rawIndustry, name: value.customIndustryName.trim() }
        : rawIndustry;
    const rawVideoType = industry.videoTypes.find(v => v.id === value.videoTypeId);
    if (!rawVideoType) return null;
    // When the user picks "Other (Custom Video)", swap the catalog name for
    // the free-text label they typed (capped at 30 chars on input).
    const videoType = (rawVideoType.id === 'other-custom' && value.customVideoTypeName?.trim())
        ? { ...rawVideoType, name: value.customVideoTypeName.trim() }
        : rawVideoType;
    return {
        industry: { id: industry.id, name: industry.name, desc: industry.desc, benefit: industry.benefit },
        videoType: { id: videoType.id, name: videoType.name, desc: videoType.desc, benefit: videoType.benefit },
        castMode: value.castMode || 'with_actors',
    };
};

export default IndustryVideoTypeCastPicker;
